import React, { useEffect, useState } from 'react';
import { Accordion, Alert, Badge, Button, Card, Col, Form, InputGroup, Modal, Row, Spinner, Table } from 'react-bootstrap';

import {
  useGetWebFormsSettings,
  usePatchWebFormsSettings,
  useGetWebFormsDefinitions,
  usePostWebFormsDefinitions,
  usePutWebFormsDefinitionsDefinitionId,
  useDeleteWebFormsDefinitionsDefinitionId,
  getWebFormsSettingsSecretsSecretName,
  usePostWebFormsSettingsTurnstileTestConnection,
  usePostWebFormsSettingsSlackTestConnection,
} from '../../api/generated/arrayscapeGamingDashboardAPI';
import type { WebFormSettings, WebFormDefinition, JsonPatchOperation, WebFormTestResult } from '../../api/generated/model';

// Secret fields are write-only. They are never returned by GET, so they are
// held in separate local state and only included in the patch when the admin
// actually typed one — otherwise saving the form would blank a stored secret.
const SECRET_KEYS = ['turnstile_site_key', 'turnstile_secret_key'] as const;
type SecretKey = typeof SECRET_KEYS[number];

const empty: WebFormSettings = {
  enabled: false,
  recipient: '',
  from_address: '',
  from_name: '',
  fallback_contact_email: '',
  subject_template: '',
  turnstile_hostnames: '',
  turnstile_timeout_ms: 10000,
  honeypot_field_name: '',
  trusted_proxy_cidrs: '',
  max_body_bytes: 16384,
  rate_limit_per_ip_hour: 20,
  rate_limit_untrusted_per_ip_hour: 5,
  rate_limit_global_hour: 100,
  quarantine_retention_days: 30,
  slack_webhook_url: '',
};

const WebFormsTab: React.FC = () => {
  const { data, isLoading, refetch } = useGetWebFormsSettings();
  const patchMutation = usePatchWebFormsSettings();

  const [form, setForm] = useState<WebFormSettings>(empty);
  const [original, setOriginal] = useState<WebFormSettings>(empty);
  const [secrets, setSecrets] = useState<Record<SecretKey, string>>({
    turnstile_site_key: '',
    turnstile_secret_key: '',
  });
  // What the server told us is currently stored, per field. Held separately
  // from `secrets` so that revealing a value does not count as an edit — the
  // input shows it, but until the admin changes it there is nothing to save.
  const [revealed, setRevealed] = useState<Record<SecretKey, string | null>>({
    turnstile_site_key: null,
    turnstile_secret_key: null,
  });
  // Whether each input is currently unmasked. Distinct from `revealed`, which
  // is the value the SERVER reports as stored — a freshly typed secret is
  // showable without anything being stored yet.
  const [shown, setShown] = useState<Record<SecretKey, boolean>>({
    turnstile_site_key: false,
    turnstile_secret_key: false,
  });
  const [revealing, setRevealing] = useState<SecretKey | null>(null);
  const turnstileTest = usePostWebFormsSettingsTurnstileTestConnection();
  const slackTest = usePostWebFormsSettingsSlackTestConnection();
  // Keyed by section so the two tests do not overwrite each other's result.
  const [testResults, setTestResults] = useState<Record<string, WebFormTestResult | null>>({});
  const [testing, setTesting] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ variant: string; text: string } | null>(null);

  useEffect(() => {
    if (data) {
      setForm(data);
      setOriginal(data);
    }
  }, [data]);

  const secretsDirty = SECRET_KEYS.some(k => secrets[k] !== '' && secrets[k] !== revealed[k]);
  const hasChanges = JSON.stringify(form) !== JSON.stringify(original) || secretsDirty;
  const set = (k: keyof WebFormSettings, v: any) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      // Send every non-secret field as an upsert ('add' creates-or-replaces)
      // so nothing is silently skipped; this is a small singleton, so it is
      // cheap. Secrets are appended only when one was actually entered.
      const patches: JsonPatchOperation[] = (Object.keys(form) as (keyof WebFormSettings)[])
        .filter(k => !SECRET_KEYS.includes(k as SecretKey))
        // *_set is server-computed; patching it back would be meaningless
        // and the backend ignores it anyway.
        .filter(k => !String(k).endsWith('_set'))
        .map(k => ({ op: 'add', path: `/${k}`, value: form[k] }));

      // Only patch a secret the admin actually changed. A value that merely
      // came back from a reveal is identical to what is stored, so sending it
      // would be a pointless round-trip of the plaintext.
      for (const k of SECRET_KEYS) {
        if (secrets[k] !== '' && secrets[k] !== revealed[k]) {
          patches.push({ op: 'add', path: `/${k}`, value: secrets[k] });
        }
      }

      await patchMutation.mutateAsync({ data: patches });
      await refetch();
      setSecrets({ turnstile_site_key: '', turnstile_secret_key: '' });
      setRevealed({ turnstile_site_key: null, turnstile_secret_key: null });
      setShown({ turnstile_site_key: false, turnstile_secret_key: false });
      setMsg({ variant: 'success', text: 'Settings saved.' });
    } catch (err: any) {
      setMsg({
        variant: 'danger',
        text: err?.response?.data?.message || err?.response?.data?.error || 'Failed to save settings',
      });
    } finally {
      setSaving(false);
    }
  };

  const textField = (label: string, k: keyof WebFormSettings, help?: string, type: string = 'text') => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control type={type} value={String(form[k] ?? '')} onChange={e => set(k, e.target.value)} />
      {help && <Form.Text muted>{help}</Form.Text>}
    </Form.Group>
  );

  const numField = (label: string, k: keyof WebFormSettings, help?: string) => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control
        type="number"
        value={Number(form[k] ?? 0)}
        onChange={e => set(k, parseInt(e.target.value || '0', 10))}
      />
      {help && <Form.Text muted>{help}</Form.Text>}
    </Form.Group>
  );

  // Tests run against SAVED settings, not what is currently typed, so an
  // unsaved edit would otherwise produce a confusing result.
  const runTest = async (key: string, fn: () => Promise<WebFormTestResult>) => {
    setTesting(key);
    setTestResults(p => ({ ...p, [key]: null }));
    try {
      const result = await fn();
      setTestResults(p => ({ ...p, [key]: result }));
    } catch (err: any) {
      setTestResults(p => ({
        ...p,
        [key]: {
          status: 'error',
          message: err?.response?.data?.message || err?.response?.data?.error || 'Could not run the test',
        },
      }));
    } finally {
      setTesting(null);
    }
  };

  const testPanel = (key: string) => {
    const result = testResults[key];
    if (!result) return null;
    return (
      <Alert
        variant={result.status === 'ok' ? 'success' : 'danger'}
        dismissible
        onClose={() => setTestResults(p => ({ ...p, [key]: null }))}
      >
        <div className="fw-semibold">{result.message}</div>
        {result.stages && result.stages.length > 0 && (
          <ul className="mb-0 mt-2 small list-unstyled">
            {result.stages.map((st, i) => (
              <li key={i}>
                <i className={`bi ${st.ok ? 'bi-check-circle text-success' : 'bi-x-circle text-danger'} me-2`}></i>
                {st.name}
                {st.detail && <span className="text-muted"> — {st.detail}</span>}
              </li>
            ))}
          </ul>
        )}
      </Alert>
    );
  };

  const testButton = (key: string, fn: () => Promise<WebFormTestResult>, note: string) => (
    <div className="d-flex align-items-center gap-2 mb-3">
      <Button
        variant="outline-primary"
        size="sm"
        onClick={() => runTest(key, fn)}
        disabled={testing !== null || hasChanges}
      >
        {testing === key
          ? <><Spinner animation="border" size="sm" className="me-1" />Testing…</>
          : <><i className="bi bi-plug me-1"></i>Test</>}
      </Button>
      <span className="text-muted small">
        {hasChanges ? 'Save your changes first — tests run against saved settings.' : note}
      </span>
    </div>
  );

  // Show serves two cases: unmask what the admin just typed (no server call
  // needed — the value is already in hand), or fetch a stored secret they
  // cannot otherwise see. Only the second requires a request.
  const toggleShow = async (k: SecretKey) => {
    if (shown[k]) {
      // Hiding must never destroy typed input. Clear only when what is
      // displayed is the value we fetched, so a server secret does not
      // linger in component state after the admin is done looking.
      if (secrets[k] !== '' && secrets[k] === revealed[k]) {
        setSecrets(p => ({ ...p, [k]: '' }));
        setRevealed(p => ({ ...p, [k]: null }));
      }
      setShown(p => ({ ...p, [k]: false }));
      return;
    }

    if (secrets[k] !== '') {
      setShown(p => ({ ...p, [k]: true }));
      return;
    }

    setRevealing(k);
    setMsg(null);
    try {
      const res = await getWebFormsSettingsSecretsSecretName(k);
      setRevealed(p => ({ ...p, [k]: res.value }));
      setSecrets(p => ({ ...p, [k]: res.value }));
      setShown(p => ({ ...p, [k]: true }));
    } catch (err: any) {
      setMsg({
        variant: 'danger',
        text: err?.response?.data?.message || err?.response?.data?.error || `Could not reveal ${k.replace(/_/g, ' ')}`,
      });
    } finally {
      setRevealing(null);
    }
  };

  const secretField = (labelText: string, k: SecretKey, isSet: boolean | undefined, help?: string) => {
    const isShown = shown[k];
    const typed = secrets[k] !== '';
    return (
      <Form.Group className="mb-3">
        <Form.Label>
          {labelText}{' '}
          {isSet
            ? <Badge bg="success">configured</Badge>
            : <Badge bg="secondary">not set</Badge>}
        </Form.Label>
        <InputGroup>
          <Form.Control
            type={isShown ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder={isSet ? 'Leave blank to keep the stored value' : ''}
            value={secrets[k]}
            onChange={e => setSecrets(p => ({ ...p, [k]: e.target.value }))}
          />
          <Button
            variant="outline-secondary"
            // Enabled whenever there is something to show: a value just
            // typed, or one stored on the server. Gating on `isSet` alone
            // left the button dead during first-time setup, which is exactly
            // when checking a pasted key matters most.
            disabled={(!isSet && !typed) || revealing === k}
            onClick={() => toggleShow(k)}
            title={isShown ? 'Hide' : typed ? 'Show what you typed' : 'Show the stored value'}
          >
            {revealing === k
              ? <Spinner animation="border" size="sm" />
              : <i className={`bi ${isShown ? 'bi-eye-slash' : 'bi-eye'}`}></i>}
          </Button>
        </InputGroup>
        {help && <Form.Text muted>{help}</Form.Text>}
      </Form.Group>
    );
  };

  if (isLoading) return <Spinner animation="border" />;

  return (
    <>
      <Card className="mb-4">
        <Card.Body>
          {msg && <Alert variant={msg.variant} dismissible onClose={() => setMsg(null)}>{msg.text}</Alert>}

          <Form.Check
            type="switch"
            className="mb-4"
            label="Accept submissions from the public website"
            checked={form.enabled}
            onChange={e => set('enabled', e.target.checked)}
          />

          <h6 className="text-muted">Delivery</h6>
          <Row>
            <Col md={6}>{textField('Recipient', 'recipient', 'The Google Group submissions are delivered to.')}</Col>
            <Col md={6}>{textField('Subject template', 'subject_template', 'Supports {label}, {first_name}, {last_name}, {form_id}. Every value is CR/LF-stripped before it reaches the header.')}</Col>

          </Row>
          <Row>
            <Col md={4}>{textField('From address', 'from_address', 'Identity these notifications are sent as. The submitter goes in Reply-To — putting them in From fails SPF and breaks DMARC alignment.')}</Col>
            <Col md={4}>{textField('From name', 'from_name', 'Optional display name.')}</Col>
            <Col md={4}>{textField('Fallback contact address', 'fallback_contact_email', 'Shown to visitors when we cannot accept their submission. Blank uses the recipient.')}</Col>
          </Row>

          <hr />
          <h6 className="text-muted">Cloudflare Turnstile</h6>
          <Row>
            <Col md={6}>
              {secretField('Site key', 'turnstile_site_key', form.turnstile_site_key_set,
                'Public by design — the website embeds it in the page. Stored encrypted anyway so both halves of the pair are handled identically. Must match the site key in the website config.json.')}
            </Col>
            <Col md={6}>
              {secretField('Secret key', 'turnstile_secret_key', form.turnstile_secret_key_set,
                'Never returned by the API and never logged. Must belong to the same widget as the site key.')}
            </Col>
          </Row>
          <Row>
            <Col md={12}>{textField('Allowed hostnames', 'turnstile_hostnames', 'Comma-separated. The domain the widget was actually solved on. Required — without it, a token minted on any site using our public site key would be accepted. Never add localhost here.')}</Col>
          </Row>
          <Row>
            <Col md={6}>{numField('Verify timeout (ms)', 'turnstile_timeout_ms', 'Aborts siteverify and fails closed. Cloudflare being unreachable must not become an open door.')}</Col>
          </Row>

          {testButton(
            'turnstile',
            () => turnstileTest.mutateAsync(),
            'Checks the secret key against Cloudflare. Cannot prove it pairs with the site key.'
          )}
          {testPanel('turnstile')}

          <hr />
          <div className="text-muted small mb-3">
            <i className="bi bi-info-circle me-1"></i>
            Delivery uses the dashboard's shared{' '}
            <a href="/admin/settings/smtp">SMTP relay</a> — configure and test the
            connection there. The From address above belongs to these notifications,
            not to the relay.
          </div>

          <hr />
          <h6 className="text-muted">Abuse controls</h6>
          <Row>
            <Col md={6}>{textField('Trusted proxy CIDRs', 'trusted_proxy_cidrs', "Comma-separated. Accepts CIDR ranges (10.0.0.0/8) and single addresses (192.168.1.5), IPv4 or IPv6, mixed freely. Only these sources are believed about X-Real-IP; blank means the forwarding headers are never trusted and every visitor looks like one client.")}</Col>
            <Col md={6}>{textField('Honeypot field name', 'honeypot_field_name', 'Only used to check the name is absent from the submitted fields. It never crosses the wire, so it does not need to stay in sync with the website.')}</Col>
          </Row>
          <Row>
            <Col md={3}>{numField('Max body bytes', 'max_body_bytes')}</Col>
            <Col md={3}>{numField('Per-IP limit / hour (known IP)', 'rate_limit_per_ip_hour', 'Submissions per hour from one public IP address, for requests that reached us through a trusted proxy so the address is the visitor’s own. An address is not a person: a school, or an entire district, can sit behind a single public IP. Set it too low and one busy customer locks out everyone sharing that address.')}</Col>
            <Col md={3}>{numField('Per-IP limit / hour (unknown IP)', 'rate_limit_untrusted_per_ip_hour', 'Submissions per hour for requests that did not come through a trusted proxy. Those only give us the connecting server’s address, which a whole upstream shares, so this stays low. Counted separately, so the two never share an allowance.')}</Col>
            <Col md={3}>{numField('Global limit / hour', 'rate_limit_global_hour')}</Col>
          </Row>
          <Row>
            <Col md={3}>{numField('Spam retention (days)', 'quarantine_retention_days', 'How long spam and undelivered submissions are kept before being purged. Accepted submissions are never purged. 0 = keep forever.')}</Col>
          </Row>

          <hr />
          <h6 className="text-muted">Slack</h6>
          <Row>
            <Col md={12}>{textField('Slack webhook URL', 'slack_webhook_url', 'Posts every accepted submission to the channel, plus delivery-failure and configuration alerts. Spam and quarantined submissions are never posted. Blank disables Slack entirely; submissions are still emailed and visible on the Form Submissions page.')}</Col>
          </Row>

          {testButton(
            'slack',
            () => slackTest.mutateAsync(),
            'Posts a real message to the channel.'
          )}
          {testPanel('slack')}

          <div className="d-flex justify-content-end pt-2 border-top">
            <Button
              variant="outline-secondary"
              className="me-2"
              disabled={!hasChanges || saving}
              onClick={() => {
                setForm(original);
                setSecrets({ turnstile_site_key: '', turnstile_secret_key: '' });
                setRevealed({ turnstile_site_key: null, turnstile_secret_key: null });
                setShown({ turnstile_site_key: false, turnstile_secret_key: false });
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" disabled={!hasChanges || saving} onClick={save}>
              {saving ? <><Spinner animation="border" size="sm" className="me-1" />Saving…</> : 'Save'}
            </Button>
          </div>
        </Card.Body>
      </Card>

      <WebFormDefinitionsCard />
    </>
  );
};

// WebFormDefinitionsCard manages the formId -> label map. The label is what
// goes in the mail subject; the browser deliberately never sends one, because
// client text reaching a header is an injection vector.
const WebFormDefinitionsCard: React.FC = () => {
  const { data, isLoading, refetch } = useGetWebFormsDefinitions();
  const createMutation = usePostWebFormsDefinitions();
  const updateMutation = usePutWebFormsDefinitionsDefinitionId();
  const deleteMutation = useDeleteWebFormsDefinitionsDefinitionId();

  const [editing, setEditing] = useState<WebFormDefinition | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [msg, setMsg] = useState<{ variant: string; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const startCreate = () => {
    setEditing({ form_id: '', label: '', description: '' });
    setIsNew(true);
  };

  const startEdit = (def: WebFormDefinition) => {
    setEditing({ ...def });
    setIsNew(false);
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setMsg(null);
    try {
      if (isNew) {
        await createMutation.mutateAsync({ data: editing });
      } else {
        await updateMutation.mutateAsync({ definitionId: editing.id!, data: editing });
      }
      await refetch();
      setEditing(null);
    } catch (err: any) {
      setMsg({
        variant: 'danger',
        text: err?.response?.data?.message || err?.response?.data?.error || 'Failed to save form',
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (def: WebFormDefinition) => {
    setMsg(null);
    try {
      await deleteMutation.mutateAsync({ definitionId: def.id! });
      await refetch();
    } catch (err: any) {
      setMsg({
        variant: 'danger',
        text: err?.response?.data?.message || err?.response?.data?.error || 'Failed to delete form',
      });
    }
  };

  if (isLoading) return <Spinner animation="border" />;

  const defs = data || [];

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>Form labels</span>
        <Button size="sm" variant="primary" onClick={startCreate}>
          <i className="bi bi-plus-lg me-1"></i>Add form
        </Button>
      </Card.Header>
      <Card.Body>
        {msg && <Alert variant={msg.variant} dismissible onClose={() => setMsg(null)}>{msg.text}</Alert>}

        <p className="text-muted small">
          Maps the form id the website sends to the human name used in the email
          subject and the submissions list. A form id with no entry here is still
          accepted and stored — it just falls back to using the id as its label, so
          a new form on the site never starts dropping enquiries.
        </p>

        <Table hover size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Form ID</th>
              <th>Label</th>
              <th>Description</th>
              <th style={{ width: 120 }}></th>
            </tr>
          </thead>
          <tbody>
            {defs.length === 0 && (
              <tr><td colSpan={4} className="text-muted">No form labels configured.</td></tr>
            )}
            {defs.map(def => (
              <tr key={def.id}>
                <td><code>{def.form_id}</code></td>
                <td>{def.label}</td>
                <td className="text-muted small">{def.description}</td>
                <td className="text-end">
                  <Button size="sm" variant="link" onClick={() => startEdit(def)}>Edit</Button>
                  <Button size="sm" variant="link" className="text-danger" onClick={() => remove(def)}>Delete</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>

      {editing && (
        <Modal show onHide={() => setEditing(null)}>
          <Modal.Header closeButton>
            <Modal.Title>{isNew ? 'Add form' : 'Edit form'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Form ID</Form.Label>
              <Form.Control
                value={editing.form_id}
                onChange={e => setEditing({ ...editing, form_id: e.target.value })}
              />
              <Form.Text muted>Must match the id declared in the website's config.json.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Label</Form.Label>
              <Form.Control
                value={editing.label}
                onChange={e => setEditing({ ...editing, label: e.target.value })}
              />
              <Form.Text muted>Used in the email subject line.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                value={editing.description || ''}
                onChange={e => setEditing({ ...editing, description: e.target.value })}
              />
              <Form.Text muted>Admin-facing only; never reaches an email.</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={saving}>
              {saving ? <><Spinner animation="border" size="sm" className="me-1" />Saving…</> : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Card>
  );
};

export default WebFormsTab;
